import { FileText, Film, ImageOff, Paperclip, Trash2, UploadCloud } from "lucide-react";
import { TableSkeleton, useSlowEnoughToShow } from "./Skeleton";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ApiError, dashboardApi } from "../api";
import { isStaff } from "../roles";
import { useT } from "../i18n";
import type { Attachment, SessionUser } from "../types";
import { formatDate } from "../utils";

/**
 * Deliverables and briefs on a request.
 *
 * This is the component the whole UX review was about: before it, a client's
 * experience of a delivered brand film was a table row reading "28 credits,
 * Completed". Now they see the work.
 */

/**
 * The Prismic mark, drawn rather than fetched.
 *
 * BM asked for a branded placeholder wherever there is no image. Inline SVG rather
 * than the logo file on purpose: this appears in every empty frame in the product,
 * and a placeholder that costs a network round trip — and flashes broken while it
 * loads — would be worse than the grey box it replaces.
 */
/**
 * Deliverables grouped into the versions they were sent as (backlog item 5).
 *
 * A VERSION IS AN UPLOAD BATCH. A cut and its thumbnail sent together are one
 * version of the work, not two, so the server stamps a whole batch with one
 * number and this groups on it.
 *
 * `null` means delivered before versions existed. Those become v1 rather than a
 * separate "unversioned" pile — a client should never be shown a v0, or a bucket
 * whose name is a database detail.
 *
 * NEWEST FIRST, because the current cut is what somebody opening this wants. And
 * the newest is labelled "Final" ONLY when the request is completed: calling v2
 * final while the work is still running is a promise the product cannot keep.
 */
export type DeliverableVersion = { version: number; isFinal: boolean; files: Attachment[] };

export function groupByVersion(files: Attachment[], requestCompleted: boolean): DeliverableVersion[] {
  const buckets = new Map<number, Attachment[]>();
  for (const file of files) {
    const v = file.version ?? 1;
    if (!buckets.has(v)) buckets.set(v, []);
    buckets.get(v)!.push(file);
  }
  const versions = [...buckets.keys()].sort((a, b) => b - a);
  return versions.map((version, index) => ({
    version,
    // Returns the FACT, not the word. "Final" has to be translatable, and a pure
    // function that bakes in English is a pure function you cannot ship in Arabic.
    isFinal: index === 0 && requestCompleted,
    files: buckets.get(version)!.sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
  }));
}

export function PrismicMark({ size = 34 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
      <path d="M24 5 L42 38 H6 Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" opacity=".55" />
      <path d="M24 5 V38" stroke="currentColor" strokeWidth="1.2" opacity=".35" />
      <path d="M24 5 L15 38" stroke="currentColor" strokeWidth="1" opacity=".22" />
      <path d="M24 5 L33 38" stroke="currentColor" strokeWidth="1" opacity=".22" />
    </svg>
  );
}

/**
 * One frame. Shows the image when there is one, and the branded placeholder when
 * there is not — the empty state reads as deliberate rather than broken.
 */
export function DeliverableFrame({
  attachment,
  label,
  ratio = "16 / 9",
}: {
  attachment?: Attachment | null;
  label?: string;
  ratio?: string;
}) {
  const t = useT();
  const isImage = attachment?.mime.startsWith("image/");
  const isVideo = attachment?.mime.startsWith("video/");

  return (
    <div className="deliverable-frame" style={{ aspectRatio: ratio }}>
      {isImage ? (
        <img src={attachment!.url} alt={attachment!.filename} loading="lazy" />
      ) : (
        <div className="deliverable-placeholder">
          <PrismicMark />
          <span>
            {isVideo ? t("work.videoDelivered") : attachment ? t("work.documentDelivered") : label || t("work.nothingDelivered")}
          </span>
        </div>
      )}
      {attachment && !isImage && (
        <div className="deliverable-badge">{isVideo ? <Film size={14} /> : <FileText size={14} />}</div>
      )}
    </div>
  );
}

/** Keys, not words: a module constant is evaluated once at import, long before a
 *  translator exists. The component resolves them at render. */
const KIND_LABEL_KEY: Record<string, string> = { brief: "work.fromClient", deliverable: "work.deliveredByPrismic" };

export default function Deliverables({
  requestId,
  user,
  canDeliver,
  requestCompleted = false,
}: {
  requestId: string;
  user: SessionUser;
  /** Staff attach delivered work; the server enforces this regardless. */
  canDeliver: boolean;
  /** Only a COMPLETED request can have a version called "Final". Labelling v2
   *  final while the work is still running is a promise the product cannot keep. */
  requestCompleted?: boolean;
}) {
  const t = useT();
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  const briefInput = useRef<HTMLInputElement>(null);
  const deliverInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await dashboardApi.attachments(requestId);
      setItems(data.attachments);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) { setItems([]); return; }
      toast.error(error instanceof Error ? error.message : t("error.loadFiles"));
      setItems([]);
    }
  }, [requestId]);

  useEffect(() => { void load(); }, [load]);

  async function upload(files: FileList | null, kind: "brief" | "deliverable") {
    if (!files?.length) return;
    setBusy(true);
    try {
      await dashboardApi.uploadAttachments(requestId, files, kind);
      toast.success(files.length === 1 ? t("timeline.work_delivered") : `${files.length} files added`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.upload"));
    } finally {
      setBusy(false);
      if (briefInput.current) briefInput.current.value = "";
      if (deliverInput.current) deliverInput.current.value = "";
    }
  }

  async function remove(attachment: Attachment) {
    setBusy(true);
    try {
      await dashboardApi.deleteAttachment(attachment.id);
      toast.success(`${attachment.filename} removed`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.removeFile"));
    } finally {
      setBusy(false);
    }
  }

  const showSkeleton = useSlowEnoughToShow(!items);

  if (!items) return showSkeleton ? <TableSkeleton rows={2} label="Loading files" /> : null;

  const delivered = items.filter((a) => a.kind === "deliverable");
  const briefs = items.filter((a) => a.kind === "brief");
  const newest = groupByVersion(delivered, requestCompleted)[0]?.files ?? [];

  return (
    <section className="deliverables">
      <div className="deliverables-head">
        <h4>{t("work.title2")}</h4>
        <div className="deliverables-actions">
          {/* Anyone on the account can add a brief; only staff can deliver. */}
          <button type="button" className="button button-table" disabled={busy} onClick={() => briefInput.current?.click()}>
            <Paperclip size={15} /> {t("deliverables.addToBrief")}
          </button>
          <input ref={briefInput} type="file" multiple hidden accept="image/*,application/pdf,video/mp4"
            onChange={(event) => void upload(event.target.files, "brief")} />
          {canDeliver && (
            <>
              <button type="button" className="button button-primary button-table" disabled={busy} onClick={() => deliverInput.current?.click()}>
                <UploadCloud size={15} /> Attach delivered work
              </button>
              <input ref={deliverInput} type="file" multiple hidden accept="image/*,application/pdf,video/mp4"
                onChange={(event) => void upload(event.target.files, "deliverable")} />
            </>
          )}
        </div>
      </div>

      {/* The hero frame. Always present, so a request with nothing delivered shows
          the branded placeholder rather than the section vanishing — the absence
          of work is information too. */}
      {/* The hero shows the NEWEST version. Before this it took whichever image
          happened to be first, which on a re-delivered request meant the client
          opened the page and saw the cut they had already given notes on. */}
      <DeliverableFrame
        attachment={newest.find((a) => a.mime.startsWith("image/")) || newest[0]}
        label={canDeliver ? t("work.attachWhenReady") : t("work.prismicWillAdd")}
      />

      {/* Delivered work is grouped into the versions it was SENT as — v1, v2,
          Final. Briefs are not versioned: a brief is the ask, not a revision of
          the work. */}
      {groupByVersion(delivered, requestCompleted).map((group) => (
        <div key={group.version} className="deliverable-group">
          <span className="deliverable-kind">
            {t(KIND_LABEL_KEY.deliverable)}
            {/* Western "v1" in both languages — the numeral rule the rest of the
                product follows. Only the word "Final" translates. */}
            <em className={`version-chip${group.isFinal ? " version-final" : ""}`}>
              {group.isFinal ? t("work.finalVersion") : `v${group.version}`}
            </em>
            {group.files.length > 1 ? <i>{t("work.filesInVersion", { n: group.files.length })}</i> : null}
          </span>
          <ul className="deliverable-list">
            {group.files.map((attachment) => (
              <li key={attachment.id}>
                <a href={attachment.url} target="_blank" rel="noreferrer">
                  {attachment.mime.startsWith("video/") ? <Film size={15} /> : attachment.mime.startsWith("image/") ? <Paperclip size={15} /> : <FileText size={15} />}
                  <span dir="auto">{attachment.filename}</span>
                </a>
                <small>
                  {Math.max(1, Math.round(attachment.bytes / 1024))} KB
                  {attachment.uploadedByName ? ` · ${attachment.uploadedByName}` : ""}
                  {` · ${formatDate(attachment.createdAt)}`}
                </small>
                {isStaff(user.role) && (
                  <button type="button" className="deliverable-remove" disabled={busy}
                    onClick={() => void remove(attachment)} aria-label={t("aria.removeFile", { name: attachment.filename })}>
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {[["brief", briefs]].map(([kind, list]) => {
        const rows = list as Attachment[];
        if (!rows.length) return null;
        return (
          <div key={kind as string} className="deliverable-group">
            <span className="deliverable-kind">{t(KIND_LABEL_KEY[kind as string])}</span>
            <ul className="deliverable-list">
              {rows.map((attachment) => (
                <li key={attachment.id}>
                  <a href={attachment.url} target="_blank" rel="noreferrer">
                    {attachment.mime.startsWith("image/") ? <ImageOff size={15} style={{ display: "none" }} /> : null}
                    {attachment.mime.startsWith("video/") ? <Film size={15} /> : attachment.mime.startsWith("image/") ? <Paperclip size={15} /> : <FileText size={15} />}
                    <span>{attachment.filename}</span>
                  </a>
                  <small>
                    {Math.max(1, Math.round(attachment.bytes / 1024))} KB
                    {attachment.uploadedByName ? ` · ${attachment.uploadedByName}` : ""}
                    {` · ${formatDate(attachment.createdAt)}`}
                  </small>
                  {isStaff(user.role) && (
                    <button type="button" className="deliverable-remove" disabled={busy}
                      onClick={() => void remove(attachment)} aria-label={t("aria.removeFile", { name: attachment.filename })}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
